import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography, Button, Space, Input, Form,
    Divider, Row, Col, Avatar, Alert, Card, Spin, Select,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
    ArrowLeftOutlined, SafetyCertificateOutlined, InfoCircleOutlined,
    UserOutlined, CheckOutlined, CopyOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { profilsService } from '../../services/ProfileService';
import { habilitationsService } from '../../services/HabilitationsService';
import HabilitationSelector from '../../components/HabilitationSelector';

const { Title, Text } = Typography;

export default function ProfilCreate() {
    const { t } = useTranslation('profils');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [habilitations, setHabilitations] = useState([]);
    const [loadingHabs, setLoadingHabs] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const [selectedIds, setSelectedIds] = useState([]);
    const [profils, setProfils] = useState([]);
    const [modele, setModele] = useState(null);

    const loadHabilitations = useCallback(async () => {
        setLoadingHabs(true);
        try {
            const res = await habilitationsService.list();
            setHabilitations(res.data?.data || res.data || []);
        } catch {
            toast.error(i18n.t('profils:commun.erreurChargementHabilitations'));
        } finally {
            setLoadingHabs(false);
        }
    }, []);

    // Les profils existants servent de modèles : on les charge même sans copie
    // demandée, pour que le choix soit offert tout de suite.
    const loadProfils = useCallback(async () => {
        try {
            const res = await profilsService.list();
            return res.data?.data || res.data || [];
        } catch {
            return [];   // pas de modèle proposé, la création à blanc reste possible
        }
    }, []);

    /**
     * Un nom libre à partir de celui du modèle. « Copie de COMPTABLE » suffit
     * presque toujours ; s'il existe déjà, on numérote plutôt que de laisser le
     * serveur refuser l'enregistrement après coup.
     */
    const nomDisponible = (base, existants) => {
        const pris = new Set(existants.map((p) => (p.profilNom || '').trim().toLowerCase()));
        let candidat = i18n.t('profils:creation.copieDe', { nom: base });
        let n = 2;
        while (pris.has(candidat.toLowerCase())) {
            candidat = i18n.t('profils:creation.copieDeNumero', { nom: base, numero: n });
            n += 1;
        }
        return candidat;
    };

    /** Reprend le nom, le commentaire et les habilitations d'un profil existant. */
    const appliquerModele = useCallback(async (trackingId, listeProfils) => {
        if (!trackingId) return;
        try {
            const res = await profilsService.get(trackingId);
            const source = res.data?.data || res.data;
            if (!source) return;
            setSelectedIds((source.habilitations || []).map((h) => h.habilitationTrackingId));
            form.setFieldsValue({
                profilNom: nomDisponible(source.profilNom || i18n.t('profils:creation.profilParDefaut'), listeProfils || []),
                commentaire: source.commentaire || '',
            });
            setModele(source);
        } catch {
            toast.error(i18n.t('profils:creation.repriseImpossible'));
        }
    }, [form]);

    useEffect(() => {
        loadHabilitations();
        loadProfils().then((liste) => {
            setProfils(liste);
            const aCopier = searchParams.get('copier');
            if (aCopier) appliquerModele(aCopier, liste);
        });
    }, [loadHabilitations, loadProfils, appliquerModele, searchParams]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            await profilsService.create({
                profilNom: values.profilNom,
                commentaire: values.commentaire || '',
                habilitationTrackingIds: selectedIds,
                organismeTrackingId: null,
            });
            toast.success(t('creation.succes'));
            navigate('/profils');
        } catch (err) {
            if (err?.errorFields) return;
            toast.error(err?.response?.data?.message || t('creation.erreur'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout title={t('creation.titreOnglet')}>
            <div style={{ padding: '28px 32px', background: '#f9fafb', minHeight: '100vh' }}>

                {/* ── Header ── */}
                <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/profils')}
                        type="text"
                        style={{ color: '#6b7280' }}
                    />
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#111827' }}>
                            {t('creation.titre')}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {t('creation.sousTitre')}
                        </Text>
                    </div>
                </div>

                {modele ? (
                    <Alert
                        type="success" showIcon icon={<CopyOutlined />}
                        message={t('creation.modeleRepris', { nom: modele.profilNom, count: selectedIds.length })}
                        description={t('creation.modeleDescription')}
                        style={{ marginBottom: 24, fontSize: 12 }}
                    />
                ) : (
                    <Alert
                        type="info" showIcon icon={<InfoCircleOutlined />}
                        message={t('commun.aideModule')}
                        style={{ marginBottom: 24, fontSize: 12 }}
                    />
                )}

                <Form form={form} layout="vertical" requiredMark="optional">
                    <Row gutter={24}>
                        {/* Colonne infos */}
                        <Col xs={24} lg={8}>
                            <Card
                                title={
                                    <Space>
                                        <Avatar size={28} icon={<SafetyCertificateOutlined />}
                                            style={{ background: '#e8f5ed', color: '#1a6b3c' }} />
                                        <span>{t('commun.informations')}</span>
                                    </Space>
                                }
                                style={{ borderRadius: 10, border: '1px solid #e5e7eb' }}
                            >
                                <Form.Item
                                    label={t('creation.modele')}
                                    tooltip={t('creation.modeleAide')}
                                >
                                    <Select
                                        allowClear showSearch
                                        placeholder={t('creation.modelePlaceholder')}
                                        optionFilterProp="label"
                                        value={modele?.profilTrackingId}
                                        onChange={(id) => {
                                            if (!id) { setModele(null); return; }
                                            appliquerModele(id, profils);
                                        }}
                                        getPopupContainer={(t) => t.parentElement}
                                        options={profils.map((p) => ({
                                            value: p.profilTrackingId,
                                            label: t('creation.modeleOption', { nom: p.profilNom, count: (p.habilitations || []).length }),
                                        }))}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="profilNom"
                                    label={t('commun.nomProfil')}
                                    rules={[{ required: true, message: t('commun.nomObligatoire') }]}
                                    tooltip={t('commun.nomExemples')}
                                >
                                    <Input placeholder={t('commun.nomPlaceholder')} size="large" />
                                </Form.Item>

                                <Form.Item name="organisme" label={t('commun.organisme')}
                                    tooltip={t('commun.organismeAide')}>
                                    <Input disabled placeholder={t('commun.organismePlaceholder')}
                                        prefix={<UserOutlined style={{ color: '#9ca3af' }} />} />
                                </Form.Item>

                                <Form.Item name="commentaire" label={t('commun.commentaire')}>
                                    <Input.TextArea
                                        rows={4}
                                        placeholder={t('commun.commentairePlaceholder')}
                                        maxLength={500} showCount
                                    />
                                </Form.Item>
                            </Card>
                        </Col>

                        {/* Colonne habilitations */}
                        <Col xs={24} lg={16}>
                            <Spin spinning={loadingHabs} tip={t('commun.chargementHabilitations')}>
                                <Form.Item name="habilitationTrackingIds" noStyle>
                                    <HabilitationSelector
                                        habilitations={habilitations}
                                        value={selectedIds}
                                        onChange={setSelectedIds}
                                    />
                                </Form.Item>
                            </Spin>
                        </Col>
                    </Row>

                    {/* ── Actions ── */}
                    <Divider />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button style={{ background: 'red' }} onClick={() => navigate('/profils')} disabled={saving}>
                            {t('commun.annuler')}
                        </Button>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={saving}
                            disabled={saving}
                            onClick={handleSave}
                            size="large"
                            style={{ background: saving ? undefined : '#1a6b3c', minWidth: 160 }}
                        >
                            {saving ? t('commun.enregistrement') : t('creation.creer')}
                        </Button>
                    </div>
                </Form>
            </div>
        </Layout>
    );
}